import { enqueueSnackbar } from 'notistack';
import { store } from '../../redux/store';
import {
  onReceiveMessage,
  getConversations,
  getConversation,
  handleRemoteChatFinalization,
  resetActiveConversation,
  updateMessageStatus,
  updateMessageAttachments,
  updateMessageUid,
} from '../../redux/slices/chat';
import { getEvents } from '../../redux/slices/calendar';
import { PATH_DASHBOARD } from '../../routes/paths';
import { soundManager } from '../soundManager';
import { showNativeNotification } from '../notificationsPermission';

export class AgentHandlers {
  static async handleChatTransfer(dispatch, message, user) {
    const transferredChatId = message.data?.chatId;

    // Verifica se o chat foi transferido DESTE usuário
    if (message.data?.fromUserId === user._id && transferredChatId) {
      const currentActiveConversationId = store.getState().chat.activeConversationId;

      if (transferredChatId === currentActiveConversationId) {
        dispatch(resetActiveConversation());
      }

      dispatch(getConversations(user));
      await soundManager.playSound('leave');
    }
  }

  static async handleChatAssignment(dispatch, message, user) {
    if (message.data?.agentId === user._id) {
      dispatch(getConversations(user));
      await soundManager.playSound('new', message.data.chatId);
    }
  }

  static async handleIncomingMessages(dispatch, message, user) {
    const { chatId, agentId, messages = [] } = message.data || {};

    if (!messages.length) {
      return;
    }

    const { activeConversationId, conversations } = store.getState().chat;
    const isAssignedToUser = agentId === user._id;
    const isCurrentChat = chatId && chatId === activeConversationId;

    // Modificado para ser mais restritivo: se não for do usuário e não for o chat atual, ignora.
    // Isso evita que mensagens sem agentId (broadcasts ou erros) notifiquem todos os usuários.
    if (!isAssignedToUser && !isCurrentChat) {
      return;
    }

    const existingConversation = conversations.byId?.[chatId];
    const needsConversationSync =
      isCurrentChat &&
      existingConversation &&
      messages.some(
        (incomingMessage) =>
          incomingMessage.replyingTo &&
          !existingConversation.messages?.some(
            (msg) =>
              msg.uid === incomingMessage.replyingTo ||
              msg._id === incomingMessage.replyingTo ||
              (msg.internalCorrelationId && msg.internalCorrelationId === incomingMessage.replyingTo)
          )
      );

    if (needsConversationSync) {
      dispatch(getConversation(chatId));
    }

    messages.forEach((incomingMessage) => {
      const reorderConversation =
        user?.company?.config?.chat_reorder_conversations_on_new_message !== false;
      dispatch(
        onReceiveMessage({
          conversationId: chatId,
          text: incomingMessage.text,
          uid: incomingMessage.uid,
          from: incomingMessage.from,
          timestamp: incomingMessage.timestamp,
          attachments: incomingMessage.attachments,
          replyingTo: incomingMessage.replyingTo,
          replyingToMetadata: incomingMessage.replyingToMetadata,
          location: incomingMessage.location,
          contacts: incomingMessage.contacts,
          reorderConversation,
          reactionTo: incomingMessage.reactionTo,
        })
      );
    });

    const lastMessage = messages[messages.length - 1];

    await soundManager.playSound('new', lastMessage.uid);

    // Notifica apenas se não estiver na tela de chat
    if (!this.isInChatScreen(chatId)) {
      // var se tiver true na config da empresa
      const notificationMessage = user.company.config.allowNotificationsForNewMessages;

      // Verifica se a mensagem foi enviada pelo próprio usuário
      if (lastMessage.from === user._id) {
        return;
      }

      if (notificationMessage) {
        // Carrega a conversa no Redux antes de buscar o nome
        await dispatch(getConversations(user));

        // Busca a conversa no Redux state
        const state = store.getState();
        const conversation = state.chat.conversations.byId?.[chatId];

        // Busca o nome do cliente na conversa
        const clientName = conversation?.sender?.name || conversation?.sender?.phone || 'Cliente';

        // Trunca a mensagem se for muito longa (máximo 20 caracteres)
        const maxLength = 20;
        const previewSource = lastMessage.text || (lastMessage.attachments?.length ? '[Anexo]' : 'Nova mensagem');
        const truncatedMessage =
          previewSource.length > maxLength ? `${previewSource.substring(0, maxLength)}...` : previewSource;

        const chatIdForClick = chatId;

        // Notify personalizado com clique
        enqueueSnackbar(`Nova mensagem\n${clientName}: "${truncatedMessage}"`, {
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          style: {
            cursor: 'pointer',
            whiteSpace: 'pre-line',
          },
          // Adiciona um listener de clique no container
          SnackbarProps: {
            onClick: () => {
              window.location.href = `/dashboard/chat/${chatIdForClick}`;
              console.log('Toast clicado, navegando para o chat do cliente.', chatIdForClick);
            },
          },
        });

        // Exibe NOTIFICAÇÃO NATIVA do navegador
        showNativeNotification({
          title: 'Nova mensagem recebida',
          sender: clientName,
          message: lastMessage.text || previewSource,
          chatId,
        });
      } else {
        // Apenas o notify se notificações allowNotificationsForNewMessages no banco estiver desabilitada
        enqueueSnackbar('Você recebeu uma nova mensagem!', {
          variant: 'success',
          autoHideDuration: 5000,
        });
      }
    }
  }

  static handleMessageUpdate(dispatch, message) {
    const { chatId, uid, internalCorrelationId } = message.data || {};

    if (!chatId || (!uid && !internalCorrelationId)) {
      return;
    }

    if (internalCorrelationId && uid) {
      dispatch(
        updateMessageUid({
          conversationId: chatId,
          uid,
          internalCorrelationId,
        })
      );
    }

    if (!uid || !window.location.pathname.includes(chatId)) {
      return;
    }

    if (message.data.attachments) {
      dispatch(
        updateMessageAttachments({
          conversationId: chatId,
          uid,
          attachments: message.data.attachments,
        })
      );
    }

    if (message.data.status) {
      dispatch(
        updateMessageStatus({
          conversationId: chatId,
          uid,
          status: message.data.status,
        })
      );
    }
  }

  static async handleChatFinalization(dispatch, message, user) {
    if (!message.data?.chatId) {
      console.error('Socket event "chatFinalization" (agent) sem chatId:', message.data);
      return;
    }

    dispatch(handleRemoteChatFinalization(message.data, user));

    if (user._id === message.data.agentId) {
      await soundManager.playSound('leave');
    }
  }

  static handleVideoSchedule(dispatch, message) {
    if (window.location.pathname.includes(PATH_DASHBOARD.videocall.root) && message.data?.serviceId) {
      dispatch(getEvents(message.data.serviceId));
    }
  }

  static handleBreakWarning(message, user) {
    const data = message.data || {};
    const targetUserId = data.userId ? data.userId.toString?.() || data.userId : undefined;
    if (!targetUserId || user._id.toString() !== targetUserId) {
      return;
    }
    const remainingMinutes = Math.max(Math.ceil(data.remainingSeconds / 60), 0);
    const text = `Faltam cerca de ${remainingMinutes} minuto(s) para ultrapassar o limite de pausas.`;
    enqueueSnackbar(text, {
      variant: 'warning',
      autoHideDuration: 8000,
    });
    showNativeNotification({
      title: 'Aviso de pausa',
      sender: 'Sistema',
      message: text,
    });
  }

  static handleUserStatusUpdate(message, user, update) {
    if (message.data && user._id === message.data.userId) {
      const updatedUser = { ...user, ...message.data.user };
      update(updatedUser);
    }
  }

  static isInChatScreen() {
    return window.location.pathname.includes(PATH_DASHBOARD.chat.root);
  }
}
